
import { useState } from 'react';
import { Calendar, Clock, ArrowRight, PlusCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { useToast } from '@/components/ui/use-toast';
import { TimeSlot, SpecificDateAvailability } from '@/components/therapists/calendar/types';
import { useLanguage } from '@/context/LanguageContext';
import { handleFormSubmissionError, handleOfflineSubmission } from '@/utils/errorLogger';

type TrialSessionBookingProps = {
  therapistId: string;
  therapistName: string;
  trialPrice: number;
  weeklyAvailability: TimeSlot[];
  specificDates: SpecificDateAvailability[];
  existingAppointments: Date[];
  currentBalance: number;
  onBook: (details: TrialSessionDetails) => void;
  onAddFunds: () => void;
};

export type TrialSessionDetails = {
  therapistId: string;
  therapistName: string;
  date: Date;
  timeSlot: string;
};

const TrialSessionBooking = ({
  therapistId,
  therapistName,
  trialPrice,
  weeklyAvailability,
  specificDates,
  existingAppointments,
  currentBalance,
  onBook,
  onAddFunds
}: TrialSessionBookingProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [timeSlot, setTimeSlot] = useState<string | null>(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  
  const handleDateSelect = (selected: Date | undefined) => {
    setDate(selected);
    setTimeSlot(null);
    
    if (selected) {
      const dayOfWeek = selected.getDay();
      
      const specificDate = specificDates.find(sd => 
        new Date(sd.date).toDateString() === selected.toDateString()
      );
      
      if (specificDate) {
        setAvailableTimeSlots(specificDate.slots.map(slot => slot.start));
      } else {
        const slotsForDay = weeklyAvailability
          .filter(slot => slot.day === dayOfWeek)
          .map(slot => slot.start);
        
        setAvailableTimeSlots(slotsForDay);
      }
    }
  };
  
  const handleBook = async () => {
    if (!date || !timeSlot) {
      toast({
        title: t("missingInformation"),
        description: t("pleaseSelectDateAndTime"),
        variant: "destructive"
      });
      return;
    }
    
    if (currentBalance < trialPrice) {
      toast({
        title: t("insufficientFunds"),
        description: t("pleaseAddMoreFunds"),
        variant: "destructive"
      });
      return;
    }
    
    // Check network connectivity
    if (handleOfflineSubmission(toast)) {
      return;
    }
    
    try {
      setIsBooking(true);
      
      const sessionDetails: TrialSessionDetails = {
        therapistId,
        therapistName,
        date: new Date(date),
        timeSlot
      };
      
      // In a real app, we would make an API call here
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onBook(sessionDetails);
      setIsOpen(false);
      
      toast({
        title: t("trialSessionBooked"),
        description: t("trialSessionScheduled")
      });
    } catch (error) {
      handleFormSubmissionError(
        error,
        toast,
        t("bookingError"),
        t("errorBookingSession")
      );
    } finally {
      setIsBooking(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="default" className="w-full" disabled={currentBalance < trialPrice}>
          {t("bookTrialSession")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="sticky top-0 bg-white z-10 pb-2">
          <DialogTitle>{t("bookTrialSession")}</DialogTitle>
          <DialogDescription>
            {t("trySessionBefore")}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          {currentBalance < trialPrice ? (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-2">
              <p className="text-sm text-amber-800 mb-2">
                {t("balanceInsufficient")}
              </p>
              <Button 
                onClick={() => {
                  setIsOpen(false);
                  onAddFunds();
                }}
                className="w-full flex items-center justify-center"
              >
                <PlusCircle className="mr-1 h-4 w-4" />
                {t("addFundsNow")}
              </Button>
            </div>
          ) : (
            <>
              <div>
                <h3 className="font-medium mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-1" /> 
                  {t("selectDate")}
                </h3>
                <CalendarComponent
                  mode="single"
                  selected={date}
                  onSelect={handleDateSelect}
                  className="border rounded-md pointer-events-auto"
                  disabled={(date) => {
                    const now = new Date();
                    now.setHours(0, 0, 0, 0);
                    return date < now;
                  }}
                />
              </div>
              
              {date && (
                <div>
                  <h3 className="font-medium mb-2 flex items-center">
                    <Clock className="h-4 w-4 mr-1" /> 
                    {t("selectTime")}
                  </h3>
                  {availableTimeSlots.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {availableTimeSlots.map((time) => (
                        <Button
                          key={time}
                          variant={timeSlot === time ? 'default' : 'outline'}
                          className="text-sm"
                          onClick={() => setTimeSlot(time)}
                        >
                          {time}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t("noAvailableTimeSlots")}
                    </p>
                  )}
                </div>
              )}
              
              <div className="bg-muted rounded-md p-3 mt-2">
                <div className="flex justify-between items-center">
                  <span>{t("trialSessionPrice")}:</span>
                  <span className="font-medium">${trialPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span>{t("yourBalance")}:</span>
                  <span>${currentBalance.toFixed(2)}</span>
                </div>
              </div>
            </>
          )}
        </div>
        
        <DialogFooter className="sticky bottom-0 pb-0 pt-2 bg-white z-10 mt-2">
          {currentBalance >= trialPrice ? (
            <Button 
              onClick={handleBook}
              disabled={!date || !timeSlot || isBooking}
              className="w-full flex items-center justify-center"
            >
              {isBooking ? t("bookingInProgress") : t("bookSession")} 
              {!isBooking && <ArrowRight className="ml-1 h-4 w-4" />}
            </Button>
          ) : (
            <div className="w-full flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsOpen(false)} 
                className="flex-1"
              >
                {t("cancel")}
              </Button>
              <Button 
                onClick={() => {
                  setIsOpen(false);
                  onAddFunds();
                }}
                className="flex-1 flex items-center justify-center"
              >
                <PlusCircle className="mr-1 h-4 w-4" />
                {t("addFunds")}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TrialSessionBooking;
